import { Directive, EventEmitter, Input, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { LdwRouterService } from '../../../route/ldw-router.service';
import { SettingsService } from '../../../shared/settings/settings.service';
import { LDWDocument } from '../../../shared/models/LDWDocument';
import { Status } from '../../../shared/models/status';
import { WebdokuService } from '../../../web-doku/webdoku.service';
import { SelectionArguments } from '../../../shared/models/selection-arguments';
import { ConfirmDialogComponent } from '../../../shared/dialogs/confirm-dialog/confirm-dialog.component';
import { DocumentService } from '../../../shared/services/document.service';
import { ErrorDialogComponent } from '../../../shared/dialogs/error-dialog/error-dialog.component';
import { StatusSelectDialogComponent } from '../../../shared/components/status-select-dialog/status-select-dialog.component';

@Directive()
export class DocumentToolbarBaseComponent {
  constructor(
    protected dialog: MatDialog,
    protected settingsService: SettingsService,
    protected webDokuService: WebdokuService,
    protected documentService: DocumentService,
    protected routerService: LdwRouterService
  ) {}

  emptyStatus = new Status();
  @Input() document: LDWDocument | undefined = undefined;
  @Input() isReadOnly = true;
  @Input() allowStatusChange = false;
  @Input() showBookmark = false;
  @Output() closeSelected: EventEmitter<boolean> = new EventEmitter();
  @Output() statusChanged: EventEmitter<SelectionArguments> = new EventEmitter();

  get documentId(): string {
    return this.document?.id ?? '';
  }

  get hasStatus(): boolean {
    return this.webDokuService.currentStatusValues.length > 0;
  }

  get hasStatusChange(): boolean {
    return this.hasStatus && this.allowStatusChange;
  }

  get hasStatusLabel(): boolean {
    return this.hasStatus && !this.hasStatusChange;
  }

  get currentStatus(): Status {
    if (this.document == undefined || this.document.status == undefined) {
      return this.emptyStatus;
    }
    return this.document.status;
  }

  /**
   * document is protected by webdoku settings
   */
  get isProtected(): boolean {
    return !this.webDokuService.currentWebDokuRedliningAllowed;
  }

  get isEditButtonAllowed(): boolean {
    return this.isReadOnly && this.settingsService.settings.pdfSettings.isEditingEnabled();
  }

  get isHistoryButtonAllowed(): boolean {
    return this.document?.hasHistory ?? false;
  }

  get isBookmarkButtonAllowed(): boolean {
    return this.showBookmark;
  }

  get isDeleteAllowed(): boolean {
    return this.webDokuService.currentWebDokuDeleteDocumentAllowed;
  }

  get isCopyAllowed(): boolean {
    return this.webDokuService.currentWebDokuCopyDocumentAllowed;
  }

  editDocument() {
    this.routerService.navigateToDocumentEditor(this.documentId);  //ToDo: implement to jump to same page as in viewer
  }

  showInfo() {
    this.routerService.navigateToDocInfo(this.documentId);
  }

  showHistory() {
    this.routerService.navigateToDocHistory(this.documentId);
  }

  showChanges() {
    this.routerService.navigateToDocChanges(this.documentId);
  }

  statusHasChanged(args: SelectionArguments) {
    this.statusChanged.emit(args);
  }

  editStatus() {
    const dialogRef = this.dialog.open(StatusSelectDialogComponent, {
      // width: '250px',
      data: this.currentStatus.id,
    });

    dialogRef.afterClosed().subscribe((result) => {
      console.log('The dialog was closed');
      if (result !== undefined) {
        const args = new SelectionArguments();
        args.id = result;
        console.log(args);
        this.statusHasChanged(args);
      }
    });
  }

  addToBookmark() {
    // needs to be implemented
  }

  deleteDocument() {
    const confirmDialog = this.dialog.open(ConfirmDialogComponent, {
      data: {
        title: 'DOCUMENT.DLG_DELETEDOCUMENT_TITLE',
        message: 'DOCUMENT.DLG_DELETEDOCUMENT_TEXT',
        okIsDefault: 'false',
      },
    });
    confirmDialog.afterClosed().subscribe((result) => {
      if (result === true) {
        this.doDelete();
      }
    });
  }

  doDelete() {
    this.documentService.deleteDocument(this.documentId).subscribe((result) => {
      if (result) {
        this.routerService.navigateBack();
      } else {
        this.dialog.open(ErrorDialogComponent, {
          data: {
            title: 'ERROR.DLG_ERROR_TITLE',
            message: 'DOCUMENT.DLG_DELETEFAILED',
          },
        });
      }
    });
  }

  copyDocument() {
    this.routerService.navigateToDocCopy(this.documentId);
  }

  close() {
    this.closeSelected.emit(true);
  }

  showToolTip(): boolean {
    return this.settingsService.settings.viewMode.showTooltips;
  }
}
